import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as events from '../../../../lib/analytics/events';
import { AffiliateTracker } from '../../../../components/AffiliateTracker';
import CheckoutContext, { CheckoutContextProvider } from '../../../../lib/CheckoutContext';
import { MarketingOptInProvider } from '../../../../contexts/MarketingOptInContext';
import { logInternalError } from '../../../../lib/errors';
import SupplementsLocationState from '../../../../models/SupplementsLocationState';
import * as types from '../../../../types';
import OrderConfirmedBanner from '../../components/Supplements/OrderConfirmedBanner/OrderConfirmedBanner';
import styles from './Supplements.module.scss';
import { CartModeProvider, CartModes } from '../../../../lib/cartModes';
import { clearGlobalVariablesForConversionTracking } from '../../../../lib/analytics/analyticsUtils';
import ProductInfoV2 from '../../components/Supplements/ProductInfoV2/ProductInfoV2';
import classNames from 'classnames';
import { sessionActions } from '../../../../reducers/session';

export default function Supplements(): JSX.Element {
  const dispatch = useDispatch();
  const session = useSelector((state: types.AppState) => state.session);
  const { cart, checkoutActions } = useContext(CheckoutContext);
  const location = useLocation<SupplementsLocationState>();
  const locationState = location?.state;
  const { orderedCart, orderedCartPricing, orderId, applePayUserSession } = locationState ?? {};

  const [hasTrackedPageLoad, setHasTrackedPageLoad] = useState(false);

  useEffect(() => {
    // Avoids tracking multiple times if re-rendered
    if (hasTrackedPageLoad) {
      return;
    }

    // Resetting the cart below triggers a re-render, so we need to make sure we don't do it multiple (infinity) times
    if (Object.keys(cart.cartItems).length === 0) {
      return;
    }

    // On mount: Track view, clear cart
    events.supplements.viewed();
    dispatch(checkoutActions.resetCart({ resetCoupon: true }));
    dispatch(checkoutActions.resetCheckout({}));
    setHasTrackedPageLoad(true);

    if (applePayUserSession && !session) {
      dispatch(sessionActions.createSession({ session: applePayUserSession }));
    }

    if (!orderId || !orderedCart) {
      logInternalError('Supplements: Missing orderId or orderedCart in state');
    }
  }, [
    applePayUserSession,
    cart.cartItems,
    checkoutActions,
    dispatch,
    hasTrackedPageLoad,
    orderId,
    orderedCart,
    session,
  ]);

  useEffect(() => {
    // On unmount: clear tracking variables
    return () => {
      clearGlobalVariablesForConversionTracking();
    };
  }, []);

  return (
    <>
      {orderId && orderedCart && (
        <AffiliateTracker orderId={orderId} cart={orderedCart} cartPricing={orderedCartPricing} />
      )}
      <OrderConfirmedBanner email={session?.email} />
      <hr className={classNames(styles.divider, styles.upsellDivider)} />
      <>
        <CartModeProvider mode={CartModes.default}>
          <MarketingOptInProvider>
            <CheckoutContextProvider>
              <ProductInfoV2
                originalCart={orderedCart}
                originalCartPricing={orderedCartPricing}
                originalOrderId={orderId}
              />
            </CheckoutContextProvider>
          </MarketingOptInProvider>
        </CartModeProvider>
      </>
    </>
  );
}
